import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import HowToVoteRoundedIcon from "@mui/icons-material/HowToVoteRounded";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import LocationCityRoundedIcon from "@mui/icons-material/LocationCityRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CircularProgress from "@mui/material/CircularProgress";
import { getAllCommunities } from "../../api/community";
import "./ShareModal.css";

export default function ShareModal({
  open,
  onClose,
  item,
  shareType = "ballot", // "ballot" | "short"
  customShareUrl,
  onToast,
}) {
  const navigate = useNavigate();

  const [communities, setCommunities] = useState([]);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [copied, setCopied] = useState(false);
  const [systemShareLoading, setSystemShareLoading] = useState(false);

  const isBallot = shareType === "ballot" || Boolean(item?.voteId && !item?.videoId);
  const isShort = !isBallot;

  // Compute share URL
  const getShareUrl = () => {
    if (customShareUrl) return customShareUrl;
    if (isBallot && item?.voteId) {
      return `${window.location.origin}/vote?voteId=${item.voteId}`;
    }
    if (isShort && (item?.videoId || item?.id)) {
      const vid = item.videoId || item.id;
      return `${window.location.origin}/single-shorts-video?videoId=${vid}`;
    }
    return window.location.href;
  };

  const getShareTitle = () => {
    if (isBallot) return item?.title || "Civic Ballot";
    return item?.title || item?.description || "Civic Short Video";
  };

  // Fetch user's joined communities on modal open
  useEffect(() => {
    if (open) {
      setCopied(false);
      setSearchTerm("");
      fetchJoinedCommunities();

      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [open]);

  const fetchJoinedCommunities = async () => {
    try {
      setLoadingCommunities(true);
      const res = await getAllCommunities();
      if (res?.success && Array.isArray(res.data)) {
        // Filter only communities where user is a member
        const joined = res.data.filter((c) => c.isMember);
        setCommunities(joined);
      } else {
        setCommunities([]);
      }
    } catch (err) {
      console.error("Failed to load joined communities for share modal:", err);
      setCommunities([]);
    } finally {
      setLoadingCommunities(false);
    }
  };

  const filteredCommunities = communities.filter((c) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(term) ||
      (c.communityType || "").toLowerCase().includes(term) ||
      (c.city || "").toLowerCase().includes(term) ||
      (c.politicalAffiliation || "").toLowerCase().includes(term) ||
      (c.profession || "").toLowerCase().includes(term) ||
      (c.description || "").toLowerCase().includes(term)
    );
  });

  const handleSelectCommunity = (comm) => {
    const slugOrId = comm.slug || comm.communityId;
    onClose();

    navigate(`/communities/${slugOrId}`, {
      state: {
        attachBallot: isBallot ? item : null,
        attachShort: isShort ? item : null,
        autoOpenComposer: true,
      },
    });
  };

  const handleCopyLink = () => {
    const url = getShareUrl();
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true);
        if (onToast) onToast("Link copied to clipboard!");
        setTimeout(() => setCopied(false), 2500);
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
      });
  };

  const handleSystemShare = async () => {
    const url = getShareUrl();
    const title = getShareTitle();
    const shareData = {
      title: isBallot ? `Share Ballot: ${title}` : `Share Short: ${title}`,
      text: title,
      url: url,
    };

    if (navigator.share) {
      try {
        setSystemShareLoading(true);
        await navigator.share(shareData);
        onClose();
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error with navigator.share:", err);
        }
      } finally {
        setSystemShareLoading(false);
      }
    } else {
      handleCopyLink();
    }
  };

  if (!open || !item) return null;

  const renderCommunityBadge = (comm) => {
    if (comm.communityType === "City") {
      return (
        <span className="share-comm-badge badge-city">
          <LocationCityRoundedIcon style={{ fontSize: 12 }} />
          {comm.city || "City Space"}
        </span>
      );
    }
    if (comm.communityType === "Political") {
      return (
        <span className="share-comm-badge badge-political">
          <HowToVoteRoundedIcon style={{ fontSize: 12 }} />
          {comm.politicalAffiliation || "Political Circle"}
        </span>
      );
    }
    if (comm.communityType === "Profession") {
      return (
        <span className="share-comm-badge badge-profession">
          <WorkOutlineRoundedIcon style={{ fontSize: 12 }} />
          {comm.profession || "Professional Circle"}
        </span>
      );
    }
    if (comm.isGated) {
      return (
        <span className="share-comm-badge badge-gated">
          <LockRoundedIcon style={{ fontSize: 12 }} />
          {comm.requiredCertificationName || "Merit Space"}
        </span>
      );
    }
    return <span className="share-comm-badge badge-open">Open Community</span>;
  };

  return createPortal(
    <AnimatePresence>
      <div className="share-modal-overlay" onClick={onClose}>
        <motion.div
          className="share-modal-container"
          initial={{ opacity: 0, scale: 0.94, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 15 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="share-modal-header">
            <div className="share-header-title-wrap">
              <div className={`share-type-avatar ${isBallot ? "avatar-ballot" : "avatar-short"}`}>
                {isBallot ? (
                  <HowToVoteRoundedIcon style={{ fontSize: 22, color: "#48D1CC" }} />
                ) : (
                  <VideoLibraryRoundedIcon style={{ fontSize: 22, color: "#2dd4bf" }} />
                )}
              </div>
              <div>
                <h3 className="share-modal-title">
                  {isBallot ? "Share or Discuss Ballot" : "Share or Discuss Short"}
                </h3>
                <p className="share-modal-subtitle">
                  Attach to a community post or share externally
                </p>
              </div>
            </div>
            <button className="btn-share-close" onClick={onClose} title="Close">
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>

          {/* Item Preview Card */}
          <div className="share-item-preview-card">
            {isShort && (item.thumbnailUrl || item.videoUrl) ? (
              <div className="share-item-thumb-box">
                {item.thumbnailUrl ? (
                  <img
                    src={item.thumbnailUrl}
                    alt={getShareTitle()}
                    className="share-item-thumb-img"
                  />
                ) : (
                  <div className="share-item-thumb-fallback">
                    <VideoLibraryRoundedIcon style={{ fontSize: 24, color: "#2dd4bf" }} />
                  </div>
                )}
              </div>
            ) : (
              <div className="share-item-ballot-icon-box">
                <HowToVoteRoundedIcon style={{ fontSize: 26, color: "#48D1CC" }} />
              </div>
            )}
            <div className="share-item-details">
              <span className="share-item-type-tag">
                {isBallot ? "Ballot" : "Civic Short Video"}
              </span>
              <h4 className="share-item-title">{getShareTitle()}</h4>
              <p className="share-item-subtext">
                {isBallot
                  ? `${(item.votingOption || item.options || []).length} Options • ${
                      item.createdByUserName || "Civic Community"
                    }`
                  : `By ${item.userName || item.creatorName || "Civic Creator"}`}
              </p>
            </div>
          </div>

          {/* Body Content */}
          <div className="share-modal-body">
            {/* Section 1: Share to Community */}
            <div className="share-section">
              <div className="share-section-header">
                <div className="share-section-title-wrap">
                  <ForumRoundedIcon style={{ fontSize: 18, color: "#48D1CC" }} />
                  <span className="share-section-title">Post to Community</span>
                </div>
                <span className="share-section-desc">
                  Select a community you belong to attach this {isBallot ? "ballot" : "short"} and start a discussion:
                </span>
              </div>

              {/* Search if multiple communities */}
              {communities.length > 3 && (
                <div className="share-comm-search-box">
                  <SearchRoundedIcon style={{ fontSize: 18, color: "#64748b" }} />
                  <input
                    type="text"
                    placeholder="Search your joined communities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="share-comm-search-input"
                  />
                  {searchTerm && (
                    <button
                      className="btn-clear-search"
                      onClick={() => setSearchTerm("")}
                    >
                      <CloseIcon style={{ fontSize: 14 }} />
                    </button>
                  )}
                </div>
              )}

              {/* Communities List */}
              <div className="share-communities-list">
                {loadingCommunities ? (
                  <div className="share-loading-state">
                    <CircularProgress size={26} sx={{ color: "#48D1CC" }} />
                    <span>Loading your communities...</span>
                  </div>
                ) : communities.length === 0 ? (
                  <div className="share-empty-communities">
                    <div className="share-empty-icon-wrap">
                      <GroupRoundedIcon style={{ fontSize: 28, color: "#48D1CC" }} />
                    </div>
                    <h5>No Joined Communities Yet</h5>
                    <p>
                      Join local city spaces, political circles, or profession forums to attach ballots and start peer discussions.
                    </p>
                    <button
                      className="btn-explore-communities"
                      onClick={() => {
                        onClose();
                        navigate("/communities");
                      }}
                    >
                      Explore & Join Communities
                    </button>
                  </div>
                ) : filteredCommunities.length === 0 ? (
                  <div className="share-no-match">
                    <p>No joined communities match "{searchTerm}"</p>
                  </div>
                ) : (
                  filteredCommunities.map((comm) => (
                    <div
                      key={comm.communityId}
                      className="share-community-card"
                      onClick={() => handleSelectCommunity(comm)}
                    >
                      <div className="share-comm-avatar-wrap">
                        {comm.avatarUrl ? (
                          <img
                            src={comm.avatarUrl}
                            alt={comm.name}
                            className="share-comm-avatar"
                          />
                        ) : (
                          <div className="share-comm-avatar-placeholder">
                            {comm.communityType === "City" ? (
                              <LocationCityRoundedIcon style={{ fontSize: 18, color: "#14b8a6" }} />
                            ) : comm.communityType === "Political" ? (
                              <HowToVoteRoundedIcon style={{ fontSize: 18, color: "#48D1CC" }} />
                            ) : comm.communityType === "Profession" ? (
                              <WorkOutlineRoundedIcon style={{ fontSize: 18, color: "#38bdf8" }} />
                            ) : (
                              comm.name.charAt(0).toUpperCase()
                            )}
                          </div>
                        )}
                      </div>

                      <div className="share-comm-info">
                        <div className="share-comm-name-row">
                          <h5 className="share-comm-name">{comm.name}</h5>
                          {renderCommunityBadge(comm)}
                        </div>
                        <div className="share-comm-meta">
                          <span className="share-comm-members">
                            <GroupRoundedIcon style={{ fontSize: 13 }} />
                            {comm.memberCount} {comm.memberCount === 1 ? "Member" : "Members"}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn-share-to-comm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectCommunity(comm);
                        }}
                      >
                        <span>Attach & Post</span>
                        <ArrowForwardRoundedIcon style={{ fontSize: 16 }} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Section 2: External Share & Link */}
            <div className="share-section external-share-section">
              <div className="share-section-title-wrap">
                <ShareIcon style={{ fontSize: 16, color: "#94a3b8" }} />
                <span className="share-section-title">Other Sharing Options</span>
              </div>

              <div className="share-external-buttons">
                {navigator.share && (
                  <button
                    className="btn-external-share btn-apps-share"
                    onClick={handleSystemShare}
                    disabled={systemShareLoading}
                  >
                    <ShareIcon style={{ fontSize: 18 }} />
                    <span>Share to Apps</span>
                  </button>
                )}

                <button
                  className={`btn-external-share btn-copy-share ${copied ? "copied" : ""}`}
                  onClick={handleCopyLink}
                >
                  {copied ? (
                    <>
                      <CheckRoundedIcon style={{ fontSize: 18, color: "#34d399" }} />
                      <span style={{ color: "#34d399" }}>Link Copied!</span>
                    </>
                  ) : (
                    <>
                      <ContentCopyIcon style={{ fontSize: 18 }} />
                      <span>Copy Link</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
